import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CANSAssessment, AssessmentType, CANSEligibility } from '../types/cans';
import { apiGet, apiPost } from '../services/api';
import { usePrograms } from '../hooks/usePrograms';
import { formatDate } from '../utils/dateUtils';
import '../styles/FormPages.css';
import '../styles/CANSAssessment.css';

interface Individual {
  individualID: number;
  firstName: string;
  lastName: string;
  clientID: string;
  dateOfBirth: string;
  gender: string;
  raceEthnicity: string;
  zipCode: string;
}

export function CANSAssessmentPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { programs } = usePrograms();

  const [individual, setIndividual] = useState<Individual | null>(null);
  // Internal individualID used for API calls after fetching individual by clientId
  const [eligibility, setEligibility] = useState<CANSEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [assessmentType, setAssessmentType] = useState<AssessmentType>('Initial');
  const [assessmentDate, setAssessmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [program, setProgram] = useState('');

  // Domain states — use `any` because domains are built up incrementally via spread
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [lifeFunctioning, setLifeFunctioning] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [developmentalNeeds, setDevelopmentalNeeds] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [strengths, setStrengths] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [culturalFactors] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [caregiverResources] = useState<any>({ noCaregiverIdentified: false });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [behavioralEmotional] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [traumaModule] = useState<any>({ experiences: {}, symptoms: {} });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [substanceUseModule] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [riskBehaviors] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dangerToOthersModule] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sexualAggressionModule] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [runawayModule] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [delinquentBehaviorModule] = useState<any>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [fireSettingModule] = useState<any>({});

  // Rationales
  const [lifeFunctioningRationale, setLifeFunctioningRationale] = useState('');
  const [strengthsRationale, setStrengthsRationale] = useState('');
  const [culturalFactorsRationale] = useState('');
  const [caregiverResourcesRationale] = useState('');
  const [behavioralEmotionalRationale] = useState('');
  const [riskBehaviorsRationale] = useState('');

  useEffect(() => {
    fetchIndividual();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Fetch eligibility once individual is loaded
  useEffect(() => {
    if (individual?.individualID) {
      checkEligibility(individual.individualID);
    }
  }, [individual]);

  const fetchIndividual = async () => {
    try {
      const response = await apiGet<Individual>(`/individuals/by-client/${clientId}`);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch client information');
      }

      setIndividual(response.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const checkEligibility = async (individualId: number) => {
    setLoading(true);
    try {
      const response = await apiGet<CANSEligibility>(
        `/individuals/${individualId}/cans/eligibility`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to check eligibility');
      }

      setEligibility(response.data);

      if (!response.data.isEligible) {
        setError(response.data.reason ?? '');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const assessment: Partial<CANSAssessment> = {
        assessmentType,
        assessmentDate,
        program,
        lifeFunctioning,
        strengths,
        culturalFactors,
        caregiverResources,
        behavioralEmotional,
        riskBehaviors,
        lifeFunctioningRationale,
        strengthsRationale,
        culturalFactorsRationale,
        caregiverResourcesRationale,
        behavioralEmotionalRationale,
        riskBehaviorsRationale,
      };

      // Add conditional modules
      if (lifeFunctioning.developmentalIntellectual >= 1) {
        assessment.developmentalNeeds = developmentalNeeds;
      }
      if (behavioralEmotional.adjustmentToTrauma >= 1) {
        assessment.traumaModule = traumaModule;
      }
      if (behavioralEmotional.substanceUse >= 1) {
        assessment.substanceUseModule = substanceUseModule;
      }
      if (riskBehaviors.dangerToOthers >= 1) {
        assessment.dangerToOthersModule = dangerToOthersModule;
      }
      if (riskBehaviors.sexualAggression >= 1) {
        assessment.sexualAggressionModule = sexualAggressionModule;
      }
      if (riskBehaviors.runaway >= 1) {
        assessment.runawayModule = runawayModule;
      }
      if (riskBehaviors.delinquentBehavior >= 1) {
        assessment.delinquentBehaviorModule = delinquentBehaviorModule;
      }
      if (riskBehaviors.fireSetting >= 1) {
        assessment.fireSettingModule = fireSettingModule;
      }

      const response = await apiPost(`/individuals/${individual?.individualID}/cans`, assessment);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save assessment');
      }

      setSuccess('CANS Assessment saved successfully!');
      setTimeout(() => {
        navigate(`/clients/${clientId}`);
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const RatingButton = ({
    value,
    currentValue,
    onChange,
    type = 'need',
  }: {
    value: number;
    currentValue: number | null;
    onChange: (val: number | null) => void;
    type?: 'need' | 'strength';
  }) => {
    const isSelected = currentValue === value;
    let label = '';
    let color = '';

    if (type === 'need') {
      switch (value) {
        case 0:
          label = '0';
          color = '#4CAF50';
          break;
        case 1:
          label = '1';
          color = '#FFC107';
          break;
        case 2:
          label = '2';
          color = '#FF9800';
          break;
        case 3:
          label = '3';
          color = '#F44336';
          break;
      }
    } else {
      switch (value) {
        case 0:
          label = '0';
          color = '#4CAF50';
          break;
        case 1:
          label = '1';
          color = '#8BC34A';
          break;
        case 2:
          label = '2';
          color = '#FFC107';
          break;
        case 3:
          label = '3';
          color = '#FF9800';
          break;
      }
    }

    return (
      <button
        type="button"
        className={`rating-button ${isSelected ? 'selected' : ''}`}
        style={{
          backgroundColor: isSelected ? color : 'transparent',
          borderColor: color,
          color: isSelected ? 'white' : color,
        }}
        onClick={() => onChange(isSelected ? null : value)}
      >
        {label}
      </button>
    );
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!eligibility?.isEligible) {
    return (
      <div className="list-page">
        <div className="page-header">
          <h2>CANS Assessment</h2>
        </div>
        <div className="alert alert-warning">
          {error || 'This client is not eligible for CANS assessment.'}
        </div>
        <button onClick={() => navigate(-1)} className="button-secondary">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="form-page cans-assessment-page">
      <div className="page-header">
        <h2>CANS Assessment</h2>
        <p>Child and Adolescent Needs and Strengths 3.0</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <h3>Client Information</h3>
        {individual && (
          <div className="client-info-grid">
            <div>
              <strong>Name:</strong> {individual.firstName} {individual.lastName}
            </div>
            <div>
              <strong>Client ID:</strong> {individual.clientID}
            </div>
            <div>
              <strong>DOB:</strong> {formatDate(individual.dateOfBirth)}
            </div>
            <div>
              <strong>Gender:</strong> {individual.gender}
            </div>
            <div>
              <strong>Ethnicity:</strong> {individual.raceEthnicity}
            </div>
            <div>
              <strong>Zip Code:</strong> {individual.zipCode}
            </div>
            <div>
              <strong>Age Group:</strong> {eligibility.ageGroup}
            </div>
            <div>
              <strong>Assessment:</strong> {eligibility.isMandatory ? 'Mandatory' : 'Optional'}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Assessment Details */}
        <div className="card">
          <h3>Assessment Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="assessmentType">Type *</label>
              <select
                id="assessmentType"
                value={assessmentType}
                onChange={(e) => setAssessmentType(e.target.value as AssessmentType)}
                required
              >
                <option value="Initial">Initial</option>
                <option value="Scheduled Update">Scheduled Update</option>
                <option value="Major Life Event">Major Life Event</option>
                <option value="Exit">Exit</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="assessmentDate">Assessment Date *</label>
              <input
                type="date"
                id="assessmentDate"
                value={assessmentDate}
                onChange={(e) => setAssessmentDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="program">Program *</label>
              <select
                id="program"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                required
              >
                <option value="">Select program...</option>
                {programs.map((p) => (
                  <option key={p.programID} value={p.programName}>
                    {p.programName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Rating Legend */}
        <div className="card rating-legend">
          <h4>Rating Guidelines</h4>
          <div className="legend-grid">
            <div>
              <strong>For Needs:</strong>
              <ul>
                <li>
                  <span className="rating-0">0</span> - No evidence; no action needed
                </li>
                <li>
                  <span className="rating-1">1</span> - Monitoring/watchful waiting
                </li>
                <li>
                  <span className="rating-2">2</span> - Action required; interfering with
                  functioning
                </li>
                <li>
                  <span className="rating-3">3</span> - Dangerous/disabling; immediate action
                </li>
              </ul>
            </div>
            <div>
              <strong>For Strengths:</strong>
              <ul>
                <li>
                  <span className="rating-0">0</span> - Well-developed centerpiece strength
                </li>
                <li>
                  <span className="rating-1">1</span> - Identified and useful
                </li>
                <li>
                  <span className="rating-2">2</span> - Identified but needs building
                </li>
                <li>
                  <span className="rating-3">3</span> - No current strength identified
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* LIFE FUNCTIONING DOMAIN */}
        <div className="card domain-card">
          <h3>Life Functioning Domain</h3>
          <div className="rating-grid">
            {[
              { key: 'familyFunctioning', label: 'Family Functioning' },
              { key: 'livingSituation', label: 'Living Situation' },
              { key: 'socialFunctioning', label: 'Social Functioning' },
              { key: 'recreational', label: 'Recreational' },
              { key: 'developmentalIntellectual', label: 'Developmental/Intellectual' },
              { key: 'jobFunctioning', label: 'Job Functioning' },
              { key: 'legal', label: 'Legal' },
              { key: 'medicalPhysical', label: 'Medical/Physical' },
              { key: 'sexualDevelopment', label: 'Sexual Development' },
              { key: 'sleep', label: 'Sleep' },
              { key: 'schoolBehavior', label: 'School Behavior' },
              { key: 'schoolAttendance', label: 'School Attendance' },
              { key: 'schoolAchievement', label: 'School Achievement' },
              { key: 'decisionMaking', label: 'Decision Making' },
            ].map((item) => (
              <div key={item.key} className="rating-item">
                <label>{item.label}</label>
                <div className="rating-buttons">
                  {[0, 1, 2, 3].map((rating) => (
                    <RatingButton
                      key={rating}
                      value={rating}
                      currentValue={lifeFunctioning[item.key] ?? null}
                      onChange={(val) =>
                        setLifeFunctioning({ ...lifeFunctioning, [item.key]: val })
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label htmlFor="lifeFunctioningRationale">Rationale for items rated 2 or 3</label>
            <textarea
              id="lifeFunctioningRationale"
              value={lifeFunctioningRationale}
              onChange={(e) => setLifeFunctioningRationale(e.target.value)}
              rows={4}
              placeholder="Provide rationale for any item rated actionable (2 or 3)..."
            />
          </div>
        </div>

        {/* Conditional Developmental Needs Module */}
        {lifeFunctioning.developmentalIntellectual >= 1 && (
          <div className="card domain-card module-card">
            <h3>A. Developmental Needs Module</h3>
            <p className="module-note">
              Complete when Developmental/Intellectual is rated 1, 2, or 3
            </p>
            <div className="rating-grid">
              {[
                { key: 'cognitive', label: 'Cognitive' },
                { key: 'communication', label: 'Communication' },
                { key: 'developmental', label: 'Developmental' },
                { key: 'selfCareDailyLiving', label: 'Self-Care/Daily Living Skills' },
              ].map((item) => (
                <div key={item.key} className="rating-item">
                  <label>{item.label}</label>
                  <div className="rating-buttons">
                    {[0, 1, 2, 3].map((rating) => (
                      <RatingButton
                        key={rating}
                        value={rating}
                        currentValue={developmentalNeeds[item.key] ?? null}
                        onChange={(val) =>
                          setDevelopmentalNeeds({ ...developmentalNeeds, [item.key]: val })
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STRENGTHS DOMAIN */}
        <div className="card domain-card">
          <h3>Strengths Domain</h3>
          <div className="rating-grid">
            {[
              { key: 'familyStrengths', label: 'Family Strengths' },
              { key: 'interpersonal', label: 'Interpersonal' },
              { key: 'optimism', label: 'Optimism' },
              { key: 'educationalSetting', label: 'Educational Setting' },
              { key: 'vocational', label: 'Vocational' },
              { key: 'talentsInterests', label: 'Talents and Interests' },
              { key: 'spiritualReligious', label: 'Spiritual/Religious' },
              { key: 'communityLife', label: 'Community Life' },
              { key: 'relationshipPermanence', label: 'Relationship Permanence' },
              { key: 'resilience', label: 'Resilience' },
              { key: 'resourcefulness', label: 'Resourcefulness' },
              { key: 'culturalIdentity', label: 'Cultural Identity' },
              { key: 'naturalSupports', label: 'Natural Supports' },
            ].map((item) => (
              <div key={item.key} className="rating-item">
                <label>{item.label}</label>
                <div className="rating-buttons">
                  {[0, 1, 2, 3].map((rating) => (
                    <RatingButton
                      key={rating}
                      value={rating}
                      currentValue={strengths[item.key] ?? null}
                      onChange={(val) => setStrengths({ ...strengths, [item.key]: val })}
                      type="strength"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label htmlFor="strengthsRationale">
              Rationale for Centerpiece (0) and Useful (1) Strengths, as well as Strengths to Build
              (2 or 3)
            </label>
            <textarea
              id="strengthsRationale"
              value={strengthsRationale}
              onChange={(e) => setStrengthsRationale(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        {/* Note about abbreviated form */}
        <div className="alert alert-info">
          <strong>Note:</strong> This is an abbreviated CANS assessment form showing the most
          critical domains. A complete assessment includes Cultural Factors, Caregiver Resources,
          Behavioral/Emotional Needs, Risk Behaviors, and conditional modules. Save this assessment
          to continue with additional domains.
        </div>

        {/* Submit Buttons */}
        <div className="form-actions">
          <button type="submit" className="button-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="button-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
